import { SERVER_ROOT } from 'env';

const boardRoot = `${SERVER_ROOT}/api/board`;

function getShareTokenFromLocation() {
  try {
    return new URLSearchParams(window.location.search).get('token') || '';
  } catch {
    return '';
  }
}

export async function fetchBoard(personName, hashtag, { token, locale } = {}) {
  const params = new URLSearchParams({
    person_name: personName,
    hashtag,
  });
  const shareToken = token || getShareTokenFromLocation();
  if (shareToken) params.set('token', shareToken);
  // Applied only when the board is created for the first time.
  if (locale) params.set('locale', locale);
  const res = await fetch(`${boardRoot}?${params}`, { credentials: 'include' });
  if (!res.ok) {
    let body = null;
    try {
      body = await res.json();
    } catch {
      /* ignore */
    }
    const err = new Error(body?.error || res.statusText);
    err.code = body?.code;
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export async function patchBoardAccess(hashtag, accessRestriction, { rotateToken } = {}) {
  const res = await fetch(`${boardRoot}/access`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ hashtag, accessRestriction, rotateToken }),
  });
  if (!res.ok) {
    let body = null;
    try {
      body = await res.json();
    } catch {
      /* ignore */
    }
    const err = new Error(body?.error || res.statusText);
    err.code = body?.code;
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export async function rotateBoardShareToken(hashtag) {
  const res = await fetch(`${boardRoot}/access/rotate-token`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ hashtag }),
  });
  if (!res.ok) throw new Error(res.statusText);
  return res.json();
}

export async function inviteBoardCollaborator(hashtag, email) {
  const res = await fetch(`${boardRoot}/collaborators`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ hashtag, email }),
  });
  if (!res.ok) {
    let body = null;
    try {
      body = await res.json();
    } catch {
      /* ignore */
    }
    const err = new Error(body?.error || res.statusText);
    err.code = body?.code;
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export async function acceptBoardInvite(personName, hashtag) {
  const res = await fetch(`${boardRoot}/collaborators/accept`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ person_name: personName, hashtag }),
  });
  if (!res.ok) {
    let body = null;
    try {
      body = await res.json();
    } catch {
      /* ignore */
    }
    const err = new Error(body?.error || res.statusText);
    err.code = body?.code;
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export async function revokeBoardCollaborator(hashtag, { email, userId } = {}) {
  const res = await fetch(`${boardRoot}/collaborators`, {
    method: 'DELETE',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ hashtag, email, userId }),
  });
  if (!res.ok) throw new Error(res.statusText);
  return res.json();
}

export async function publishPinnedConversation(hashtag, conversation, { pinKind, messageAt } = {}) {
  const res = await fetch(`${boardRoot}/pinned-conversations`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ hashtag, conversation, pinKind, messageAt }),
  });
  if (!res.ok) throw new Error(res.statusText);
  return res.json();
}

export async function patchBoardViewport(hashtag, viewport) {
  const res = await fetch(`${boardRoot}/viewport`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ hashtag, viewport }),
  });
  if (!res.ok) throw new Error(res.statusText);
  return res.json();
}

/** Best-effort save on tab close/refresh (fetch keepalive). */
export function patchBoardViewportKeepalive(hashtag, viewport) {
  if (!hashtag || !viewport) return;
  try {
    fetch(`${boardRoot}/viewport`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ hashtag, viewport }),
      keepalive: true,
    });
  } catch {
    /* ignore */
  }
}

export async function patchBoardPlacementOps(hashtag, ops, { baseRevision } = {}) {
  const res = await fetch(`${boardRoot}/placements-ops`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ hashtag, ops, baseRevision }),
  });
  if (!res.ok) {
    const err = new Error(res.statusText);
    err.status = res.status;
    try { err.body = await res.json(); } catch {}
    throw err;
  }
  return res.json();
}

export async function patchBoardFrameOps(hashtag, ops, { baseRevision } = {}) {
  const res = await fetch(`${boardRoot}/frames-ops`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ hashtag, ops, baseRevision }),
  });
  if (!res.ok) {
    const err = new Error(res.statusText);
    err.status = res.status;
    try { err.body = await res.json(); } catch {}
    throw err;
  }
  return res.json();
}

export function patchBoardPlacementOpsKeepalive(hashtag, ops, { baseRevision } = {}) {
  if (!hashtag || !Array.isArray(ops) || !ops.length) return;
  try {
    fetch(`${boardRoot}/placements-ops`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ hashtag, ops, baseRevision }),
      keepalive: true,
    });
  } catch {
    /* ignore */
  }
}

export async function patchYouTubeMoment(hashtag, activityId, seconds, imageDataUrl) {
  const res = await fetch(`${boardRoot}/youtube-moment`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      hashtag,
      activityId,
      seconds,
      ...(imageDataUrl ? { imageDataUrl } : {}),
    }),
  });
  if (!res.ok) throw new Error(res.statusText || 'Failed to save YouTube moment');
  return res.json();
}

export async function patchPinEpistemicType(hashtag, activityId, epistemicType) {
  const res = await fetch(`${boardRoot}/pin-epistemic-type`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ hashtag, activityId, epistemicType }),
  });
  if (!res.ok) throw new Error(res.statusText || 'Failed to update epistemic type');
  return res.json();
}

export async function patchBoardDescription(hashtag, description) {
  const res = await fetch(`${boardRoot}/description`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ hashtag, boardGoal: description, description }),
  });
  if (!res.ok) throw new Error(res.statusText);
  return res.json();
}

export async function patchBoardLocale(hashtag, locale) {
  const res = await fetch(`${boardRoot}/locale`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ hashtag, locale }),
  });
  if (!res.ok) throw new Error(res.statusText);
  return res.json();
}

export async function patchBoardSnapshot(hashtag, tldrawSnapshot, { baseRevision } = {}) {
  const res = await fetch(`${boardRoot}/snapshot`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ hashtag, tldrawSnapshot, baseRevision }),
  });
  if (!res.ok) {
    const err = new Error(res.statusText);
    err.status = res.status;
    try { err.body = await res.json(); } catch {}
    throw err;
  }
  return res.json();
}

export async function patchBoardOps(hashtag, ops, { baseRevision } = {}) {
  const res = await fetch(`${boardRoot}/ops`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ hashtag, ops, baseRevision }),
  });
  if (!res.ok) {
    const err = new Error(res.statusText);
    err.status = res.status;
    try { err.body = await res.json(); } catch {}
    throw err;
  }
  return res.json();
}

export async function deleteBoard(hashtag) {
  const res = await fetch(`${boardRoot}?hashtag=${encodeURIComponent(hashtag)}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    let body = null;
    try {
      body = await res.json();
    } catch {
      /* ignore */
    }
    const err = new Error(body?.error || res.statusText || 'Failed to delete canvas');
    err.code = body?.code;
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export async function deleteActivityById(activityId) {
  const id = String(activityId || '');
  if (!id) throw new Error('activityId required');
  const res = await fetch(
    `${SERVER_ROOT}/api/activity?activityId=${encodeURIComponent(id)}`,
    { method: 'DELETE', credentials: 'include' }
  );
  if (!res.ok) throw new Error(res.statusText || 'Failed to delete activity');
  try {
    return await res.json();
  } catch {
    return { ok: true };
  }
}
